import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Col, Flex, Form, Input, Row, Select, Skeleton, Tooltip, Typography } from 'antd'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generateValidationSchema } from './validation'
import { ErrorMessage } from '@hookform/error-message'
import SelectCpn from './SelectCpn'
import { postAddACHExceptionRule } from 'src/app/shared/services/ACHExceptionRuleService'
import { creaditOrDebitOptions, ruleTypeOptions } from './constant'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import ConfirmModal from './ConfirmModal'

import CommonAmount from './CommonAmount'
import SelectWithSearch from './SelectWithSearch'
import { convertAllSecCode, onlyAlphaAndNumeric } from 'src/app/shared/utils'
import { debounce } from 'lodash'
import CommonModal from 'src/app/shared/components/modal/common-modal'
import './index.scss'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { getAccountList, getClientListName } from 'src/app/shared/services/ClientSetupService'

const binding: any = {
  ClientName: 'clientNames',
  AccountName: 'accountNames',
  RuleType: 'ruleType',
  CreditDebit: 'creditOrDebit',
  AmountTo: 'amountTo',
  AmountFrom: 'amountFrom',
  Description: 'ruleDescription',
  ACHOriginatorID: 'achOriginatorId',
}

const AddACHModal = forwardRef((props: any) => {
  const { t } = useTranslation()
  const { isOpen, onClose, achPlaceholders, api } = props
  const lookupValues = useSelector(getLookupValues)
  const getCreditDebitCode = (creditDebitValue: string) => {
    return creaditOrDebitOptions.find((option: any) => option?.label === creditDebitValue)?.value
  }

  const achPlaceholdersTransformed = useMemo(() => {
    return {
      clientNames: achPlaceholders.clientNames,
      accountNames: achPlaceholders.accountNames,
      achOriginatorId: achPlaceholders.achOriginatorId,
      secCode: achPlaceholders.secCode ? [achPlaceholders.secCode] : [],
      creditOrDebit: getCreditDebitCode(achPlaceholders.creditOrDebit),
      amountFrom: Number(achPlaceholders.amount).toFixed(3).slice(0, -1),
      amountTo: Number(achPlaceholders.amount).toFixed(3).slice(0, -1),
      allAccounts: !achPlaceholders.accountNames?.length,
    }
  }, [achPlaceholders])

  const [clientList, setClientList] = useState<any[]>([])
  const [accountList, setAccountList] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isFetched, setIsFetched] = useState(true)
  const [secCode, setSecCode] = useState([])
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [duplicateRule, setDuplicateRule] = useState<string>('')
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const filterOption = (input: string, option?: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const defaultValues = {
    clientNames: null,
    accountNames: [],
    allAccounts: true,
    ruleDescription: '',
    ruleType: null,
    achOriginatorId: '',
    secCode: [],
    creditOrDebit: null,
    amountFrom: null,
    amountTo: null,
  }

  const {
    control,
    formState: { errors, touchedFields, dirtyFields },
    handleSubmit,
    getValues,
    clearErrors,
    trigger,
    setValue,
    setError,
    reset,
  } = useForm<any>({
    mode: 'all',
    defaultValues: { ...defaultValues, ...achPlaceholdersTransformed },
    resolver: yupResolver(generateValidationSchema(t)),
  })

  useEffect(() => {
    if (achPlaceholdersTransformed) {
      reset({ ...defaultValues, ...achPlaceholdersTransformed })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achPlaceholdersTransformed, reset])

  const isSaveButtonDisabled =
    !isChanged ||
    (!Object.keys(touchedFields)?.length && !Object.keys(dirtyFields)?.length) ||
    !!Object.keys(errors)?.length ||
    !getValues('clientNames') ||
    (!getValues('accountNames')?.length && !getValues('allAccounts')) ||
    getValues('ruleType') === null ||
    getValues('creditOrDebit') === null

  const handleClose = () => {
    setIsOpenModal(false)
  }

  const onSubmit = async (data: any) => {
    const payload = {
      clientIds: data.clientNames ? [data.clientNames] : [],
      accountIds: data.accountNames?.map((item: any) => (item instanceof Object ? item.value : item)) || [],
      allAccount: data.allAccounts,
      description: data.ruleDescription,
      ruleType: Number(data.ruleType),
      achOriginatorID: data.achOriginatorId ? data.achOriginatorId : null,
      secCodeIds: data.secCode?.map((item: any) => (item instanceof Object ? item.value : item)) || [],
      creditDebit: Number(data.creditOrDebit),
      amountFrom: data?.amountFrom ? Number(data.amountFrom) : null,
      amountTo: data?.amountTo ? Number(data.amountTo) : null,
    }

    setLoading(true)

    postAddACHExceptionRule(payload)
      .then((res: any) => {
        setLoading(false)

        if (res?.data) {
          api.success({
            message: (
              <RichTextField
                richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.ACH_EXCEPTION_RULE_CREATED_SUCCESSFULLY}`, {
                  Rule_Description: data.ruleDescription,
                })}
              />
            ),
          })
          onClose()
        }
      })
      .catch((error) => {
        setLoading(false)
        if (error?.type === 'InlineError') {
          Object.entries(error?.errors)?.map((entry: any) => {
            if (entry[1][0] === 'PPL25') {
              setError(`${binding[entry[0]]}`, {
                message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.AT_LEAST_ONE_ACCOUNT_PER_CLIENT}`, {
                  Client_Names: clientList
                    .filter((e: any) => error?.missForClientIds.includes(e?.value))
                    .map((item: any) => item.label)
                    ?.join(','),
                }),
              })
            } else if (entry[1][0] === 'PPA05' && entry[0] === 'ACHOriginatorID') {
              setError(`${binding[entry[0]]}`, {
                message: t(`SystemMessages:PPA05`, { Object: t('ACHExceptionRule:achOriginatorId.title') }),
              })
            } else setError(`${binding[entry[0]]}`, { message: t(`SystemMessages:${entry?.[1]?.[0]}`) })
          })
        } else if (error?.type === 'ToastWarning') {
          setIsOpenModal(true)

          Object.entries(error?.errors)?.map((entry: any) => {
            setDuplicateRule(entry?.[1]?.[0])
          })
        } else {
          Object.values(error?.errors)?.map((message: any) => {
            api.error({
              message: message[0],
              placement: 'topRight',
            })
          })
        }
      })
  }

  const handleOnchangeClient = (e: any, clear: boolean) => {
    if (clear) {
      setValue('accountNames', [])
      clearErrors('accountNames')
    }
    setAccountLoading(true)
    getAccountList(e).then((res: any) => {
      if (res?.data) {
        setAccountList(res.data?.map((item: any) => ({ label: item?.accountName, value: item?.accountId })))
        setAccountLoading(false)
      }
      setIsFetched(true)
    })
  }

  const debounceSearchStateFn = useCallback(debounce(handleOnchangeClient, 500), [])

  useEffect(() => {
    if (isOpen) {
      setIsFetched(false)
      getClientListName().then(async (res: any) => {
        if (res?.data) {
          setClientList(
            res?.data?.map((item: any) => ({
              label: item.clientName,
              value: item.id,
            })),
          )

          debounceSearchStateFn([achPlaceholdersTransformed.clientNames], false)
        }
      })

      if (lookupValues && lookupValues?.SECCode?.length > 0) {
        const data = convertAllSecCode(lookupValues?.SECCode)

        setValue(
          'secCode',
          data
            ?.map((item: any) => ({
              label: item.value,
              value: item.id,
            }))
            .filter((item: any) => item.label === achPlaceholders.secCode),
        )

        setSecCode(
          data?.map((item: any) => ({
            label: item.value,
            value: item.id,
          })) as any,
        )
      }
    } else {
      reset()
    }
  }, [isOpen, lookupValues, achPlaceholders, achPlaceholdersTransformed, reset, setValue, debounceSearchStateFn])

  return (
    <CommonModal
      title={t('ACHExceptionRule:addACHModal.title')}
      width={708}
      open={isOpen}
      size='middle'
      onClose={onClose}
      centered
      className='ach-modal'
      footer={[
        <Flex key='button-group-ach-add-modal' className='button-group-ach-add-modal' justify='flex-end' gap={24}>
          <Button className='cancel-button' onClick={onClose}>
            {t('ACHExceptionRule:button.cancel')}
          </Button>

          <Button
            className='save-button'
            type='primary'
            disabled={isSaveButtonDisabled}
            htmlType='button'
            onClick={handleSubmit((data) => onSubmit(data))}
            loading={loading}
            style={loading ? { width: 150 } : {}}
          >
            {t('ACHExceptionRule:button.saveChanges')}
          </Button>
        </Flex>,
      ]}
    >
      <Form
        name='add-modal-exception-rule-form'
        className='form-container add-modal-exception-rule-form'
        layout='vertical'
      >
        {isFetched ? (
          <Flex vertical>
            <Form.Item label={t('ACHExceptionRule:client.selectClient')} className='isRequired'>
              <Controller
                name='clientNames'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    showSearch
                    style={{ width: '100%' }}
                    maxTagCount='responsive'
                    options={clientList}
                    status={errors?.clientNames ? 'error' : ''}
                    placeholder={t('ACHExceptionRule:client.placeholder')}
                    onChange={(e) => {
                      field.onChange(e)
                      setIsChanged(true)
                      debounceSearchStateFn([e], true)
                    }}
                    maxTagPlaceholder={(omittedValues) => (
                      <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                        <span>+{omittedValues?.length}</span>
                      </Tooltip>
                    )}
                    filterOption={filterOption}
                    filterSort={(a, b) => a?.label?.localeCompare(b.label)}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='clientNames' />
              </Typography>
            </Form.Item>

            <SelectCpn
              control={control}
              errors={errors}
              title={t('ACHExceptionRule:account.selectAccount')}
              checkboxTitle={t('ACHExceptionRule:account.allAccounts')}
              tooltipTitle={t('ACHExceptionRule:account.tooltip')}
              placeholder={t('ACHExceptionRule:account.placeholder')}
              options={accountList}
              selectName='accountNames'
              defaultSelectedValue={[]}
              checkboxName='allAccounts'
              defaultCheckboxValue={getValues('allAccounts')}
              clearErrors={clearErrors}
              trigger={trigger}
              setValue={setValue}
              onCheckboxChange={(e: any) => setIsChanged(true)}
              onSelectChange={(e: any) => {
                setIsChanged(true)
              }}
              onSelectBlur={() => {
                trigger('accountNames')
              }}
              loading={accountLoading}
            />

            <Form.Item label={t('ACHExceptionRule:ruleDescription.title')}>
              <Controller
                name='ruleDescription'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    status={errors?.ruleDescription ? 'error' : ''}
                    placeholder={t('ACHExceptionRule:ruleDescription.placeholder')}
                    onChange={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trimStart()
                      field.onChange(e1)
                      setIsChanged(true)
                    }}
                    onBlur={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trim()
                      field.onChange(e1)
                      setIsChanged(true)
                    }}
                    maxLength={255}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='ruleDescription' />
              </Typography>
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:achOriginatorId.title')}>
                  <Controller
                    name='achOriginatorId'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        status={errors?.achOriginatorId ? 'error' : ''}
                        placeholder={t('ACHExceptionRule:achOriginatorId.placeholder')}
                        onChange={(e) => field.onChange(onlyAlphaAndNumeric(e, 10), { shouldValidate: true })}
                        onBlur={(e) => {
                          let e1 = e
                          e1.target.value = e.target.value.trim()
                          field.onChange(e1)
                          setIsChanged(true)
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='achOriginatorId' />
                  </Typography>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:ruleType.title')} className='isRequired'>
                  <Controller
                    name='ruleType'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        style={{ width: '100%' }}
                        maxTagCount='responsive'
                        options={ruleTypeOptions}
                        status={errors?.ruleType ? 'error' : ''}
                        placeholder={t('ACHExceptionRule:ruleType.placeholder')}
                        onChange={(e) => {
                          field.onChange(e)
                          setIsChanged(true)
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='ruleType' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:secCode.title')}>
                  <SelectWithSearch
                    options={secCode}
                    control={control}
                    disabled={false}
                    name='secCode'
                    placeholder={t('ACHExceptionRule:secCode.placeholder')}
                    defaultValue={[]}
                    clearErrors={clearErrors}
                    errors={errors}
                    trigger={trigger}
                    onChange={() => setIsChanged(true)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:creditOrDebit.title')} className='isRequired'>
                  <Controller
                    name='creditOrDebit'
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        style={{ width: '100%' }}
                        maxTagCount='responsive'
                        options={creaditOrDebitOptions}
                        status={errors?.creditOrDebit ? 'error' : ''}
                        placeholder={t('ACHExceptionRule:creditOrDebit.placeholder')}
                        onChange={(e) => {
                          field.onChange(e)
                          setIsChanged(true)
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='creditOrDebit' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:amountFrom.title')}>
                  <CommonAmount
                    control={control}
                    name={'amountFrom'}
                    placeholder={t('ACHExceptionRule:amountFrom.placeholder')}
                    integerLimit={14}
                    decimalLimit={2}
                    handleTrigger={() => getValues('amountTo') && trigger('amountTo')}
                    onChange={() => setIsChanged(true)}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='amountFrom' />
                  </Typography>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label={t('ACHExceptionRule:amountTo.title')}>
                  <CommonAmount
                    control={control}
                    name={'amountTo'}
                    placeholder={t('ACHExceptionRule:amountTo.placeholder')}
                    integerLimit={14}
                    decimalLimit={2}
                    onChange={() => setIsChanged(true)}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='amountTo' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>
          </Flex>
        ) : (
          <Skeleton active />
        )}

        <ConfirmModal
          isOpen={isOpenModal}
          onClose={handleClose}
          ruleDescription={duplicateRule}
          onConfirm={handleClose}
        />
      </Form>
    </CommonModal>
  )
})

export default AddACHModal
